import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ThreadsService } from '../services/threads/threads.service';
import { MessageCrudService } from '../services/messages/message-crud.service';
import { useAIOperationWrapper } from '@/hooks/ai-usage/useAIOperationWrapper';
import { logInfo, logError, LogCategory, startMeasure, endMeasure } from '@/utils/logger';

/**
 * Hook for chat initialization operations
 * ✅ SIMPLIFIED: No longer manages thread ID state - only handles initialization logic
 */
export function useChatInitialization(
  threadId: string | null,
  onThreadIdChange: (threadId: string | null) => void, // Callback instead of setter
  setMessages: (messages: any[] | ((prev: any[]) => any[])) => void,
  loadMessages: (threadId: string) => Promise<void>,
  user: any,
  currentAssistant?: any
) {
  const queryClient = useQueryClient();
  const { executeAIOperation } = useAIOperationWrapper();

  /**
   * Initialize chat by loading existing messages if threadId exists, or create new thread
   */
  const initializeChat = useCallback(async (targetThreadId?: string): Promise<string | null> => {
    const startTime = startMeasure('initializeChat');
    
    logInfo('Starting chat initialization', { 
      category: LogCategory.STATE,
      data: { targetThreadId, hasUser: !!user }
    });

    // If no targetThreadId provided, create new thread with both database and OpenAI
    if (!targetThreadId) {
      logInfo('Creating new conversation with full thread setup', { 
        category: LogCategory.STATE,
        data: { hasUser: !!user, assistantName: currentAssistant?.name }
      });

      if (!user) {
        logError('Cannot create thread: user not authenticated', { 
          category: LogCategory.STATE 
        });
        setMessages([]);
        onThreadIdChange(null); // Use callback instead of direct setter
        endMeasure(startTime, 'Chat initialization failed (no user)', LogCategory.STATE);
        return null;
      }

      try {
        // Create new thread with OpenAI integration
        const newThread = await executeAIOperation(async () => {
          return await ThreadsService.createThreadWithOpenAI({
            userId: user.id,
            title: 'New Conversation',
            lastMsgFromUser: '',
            isPinned: false,
            isArchived: false,
            metadata: {
              assistantId: currentAssistant?.id,
              assistantName: currentAssistant?.name
            }
          });
        });

        if (newThread) {
          // Clear any existing messages for clean start
          setMessages([]);
          onThreadIdChange(newThread.id); // Use callback
          
          // ✅ CACHE INVALIDATION: Emit event for cross-context synchronization
          window.dispatchEvent(new CustomEvent('localStorage-change', {
            detail: { key: 'newThreadCreated', threadId: newThread.id }
          }));
          
          logInfo('New conversation created successfully', { 
            category: LogCategory.STATE,
            data: { threadId: newThread.id, openaiThreadId: newThread.openaiThreadId }
          });

          endMeasure(startTime, 'Chat initialization (new conversation)', LogCategory.STATE);
          return newThread.id;
        } else {
          throw new Error('Failed to create new thread');
        }

      } catch (error: any) {
        logError('Failed to create new conversation', {
          category: LogCategory.STATE,
          data: { error: error.message }
        });
        setMessages([]);
        onThreadIdChange(null); // Use callback
        endMeasure(startTime, 'Chat initialization failed (create thread)', LogCategory.STATE);
        throw new Error(`Failed to create conversation: ${error.message}`);
      }
    }

    // Load existing conversation
    logInfo('Loading existing conversation', { 
      category: LogCategory.STATE,
      data: { threadId: targetThreadId }
    });

    try {
      await loadMessages(targetThreadId);
      onThreadIdChange(targetThreadId); // Use callback
      
      logInfo('Existing conversation loaded successfully', { 
        category: LogCategory.STATE,
        data: { threadId: targetThreadId }
      });

      endMeasure(startTime, 'Chat initialization (existing conversation)', LogCategory.STATE);
      return targetThreadId;

    } catch (error: any) {
      logError('Failed to load existing conversation', {
        category: LogCategory.STATE,
        data: { error: error.message, threadId: targetThreadId }
      });
      setMessages([]);
      onThreadIdChange(null); // Use callback
      endMeasure(startTime, 'Chat initialization failed (load messages)', LogCategory.STATE);
      throw new Error(`Failed to load conversation: ${error.message}`);
    }
  }, [user, currentAssistant, onThreadIdChange, setMessages, loadMessages, queryClient, executeAIOperation]);

  return { initializeChat };
}
